import React from 'react';
import typography from '../../utils/typography';
import colors from '../../utils/colors';

const EmptyWaveform = ({ 
  onClose,
  className = "",
  isRecording = false,
  recordingWaveformData = [],
  recordingProgress = 0,
  showCloseButton = true
}) => {
  return (
    <div 
      className={`w-full h-20 relative border rounded-xl ${className}`}
      style={{
        borderColor: isRecording ? colors.states.danger : 'rgba(255, 255, 255, 0.1)',
        backgroundColor: isRecording ? `${colors.states.danger}15` : 'transparent'
      }}
    >
      {/* Recording/Empty waveform bars */}
      <div className="w-full h-12 absolute left-0 top-4 overflow-hidden px-3">
        <div className="flex justify-start items-center h-12 w-full overflow-hidden gap-0.5">
          {isRecording && recordingWaveformData.length > 0 ? (
            // Show live recording waveform with proper styling
            recordingWaveformData.map((height, index) => {
              const barHeight = Math.max(8, Math.min(48, height));
              return (
                <div 
                  key={index}
                  className="w-px rounded-[100px]" 
                  style={{ 
                    height: `${barHeight}px`,
                    backgroundColor: colors.text.inactive
                  }}
                />
              );
            })
          ) : (
            // Show empty placeholder bars
            Array.from({ length: 140 }, (_, index) => (
              <div 
                key={index}
                className="w-px rounded-[100px]" 
                style={{ 
                  height: '2px',
                  backgroundColor: colors.text.inactive,
                  opacity: 0.3
                }}
              />
            ))
          )}
        </div>
      </div>
      
      {/* Recording progress line - same style as RecordedWaveform */}
      {isRecording && (
        <>
          {/* Invisible drag area */}
          <div 
            className="absolute top-0 bottom-0 w-3 cursor-grab transition-all duration-100 ease-linear"
            style={{ 
              left: `calc(0.75rem + ${recordingProgress} * (100% - 1.5rem) - 6px)`
            }}
          />
          {/* Visible progress line */}
          <div 
            className="absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none transition-all duration-100 ease-linear"
            style={{ 
              left: `calc(0.75rem + ${recordingProgress} * (100% - 1.5rem))`
            }}
          />
        </>
      )}
      
      {/* Helper text with microphone icon - only show when not recording */}
      {!isRecording && (
        <>
          {/* Vertical line indicator */}
          <svg
            width={2}
            height={78}
            viewBox="0 0 2 78"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="absolute left-[9.5px] top-[-0.5px]"
            preserveAspectRatio="none"
          >
            <line x1={1} y1="-4.37114e-8" x2={1} y2={78} stroke="white" strokeWidth={2} />
          </svg>
          
          {/* Helper text with microphone icon */}
          <div className="flex justify-start items-center absolute left-1/2 transform -translate-x-1/2 top-14 gap-1">
            <p className="flex-grow-0 flex-shrink-0 text-left text-[#726e6c]" style={typography.timecode}>TAP</p>
            <svg
              width={16}
              height={16}
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="flex-grow-0 flex-shrink-0 w-4 h-4 relative"
              preserveAspectRatio="xMidYMid meet"
            >
              <path
                d="M7.96479 9.33337C7.41146 9.33337 6.93813 9.14004 6.55146 8.74671C6.16479 8.36004 5.96479 7.88671 5.96479 7.33337V3.33337C5.96479 2.78004 6.15813 2.30671 6.55146 1.92004C6.93813 1.53337 7.41146 1.33337 7.96479 1.33337C8.51813 1.33337 8.99146 1.52671 9.37813 1.92004C9.76479 2.31337 9.96479 2.78004 9.96479 3.33337V7.33337C9.96479 7.88671 9.77146 8.36004 9.37813 8.74671C8.98479 9.13337 8.51813 9.33337 7.96479 9.33337ZM7.96479 14C7.59813 14 7.29813 13.7 7.29813 13.3334V12.5334C7.29813 12.2 7.05146 11.92 6.72479 11.84C5.82479 11.6067 5.05813 11.1267 4.43146 10.4C3.82479 9.69337 3.45813 8.89337 3.33813 8.00004C3.29146 7.63337 3.59146 7.33337 3.96479 7.33337C4.33146 7.33337 4.62479 7.63337 4.69146 7.99337C4.81146 8.63337 5.11813 9.20004 5.60479 9.68671C6.25813 10.3334 7.03813 10.66 7.96479 10.66C8.89146 10.66 9.67146 10.3334 10.3248 9.68671C10.8115 9.20004 11.1181 8.63337 11.2381 7.99337C11.3048 7.63337 11.5981 7.33337 11.9648 7.33337C12.3315 7.33337 12.6381 7.63337 12.5915 8.00004C12.4715 8.89337 12.1115 9.69337 11.4981 10.4C10.8715 11.1267 10.1048 11.6067 9.2048 11.84C8.87813 11.92 8.63146 12.2 8.63146 12.5334V13.3334C8.63146 13.7 8.33146 14 7.96479 14Z"
                fill="#F8441B"
              />
            </svg>
            <p className="flex-grow-0 flex-shrink-0 text-left text-[#726e6c]" style={typography.timecode}>TO RECORD</p>
          </div>
        </>
      )}
      
      {/* Close button - hidden during recording or when showCloseButton is false */}
      {!isRecording && showCloseButton && (
        <div className="flex justify-center items-center w-10 h-10 absolute right-2 bottom-2 gap-1 px-4 rounded-[100px] bg-white/[0.04] backdrop-blur-[50px] cursor-pointer hover:bg-white/[0.08] transition-colors" onClick={onClose}>
        <svg
          width={16}
          height={16}
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="flex-grow-0 flex-shrink-0 w-4 h-4 relative"
          preserveAspectRatio="xMidYMid meet"
        >
          <path
            d="M8 8.93337L4.73334 12.2C4.61111 12.3223 4.45556 12.3834 4.26667 12.3834C4.07778 12.3834 3.92222 12.3223 3.8 12.2C3.67778 12.0778 3.61667 11.9223 3.61667 11.7334C3.61667 11.5445 3.67778 11.3889 3.8 11.2667L7.06667 8.00003L3.8 4.73337C3.67778 4.61114 3.61667 4.45559 3.61667 4.2667C3.61667 4.07781 3.67778 3.92225 3.8 3.80003C3.92222 3.67781 4.07778 3.6167 4.26667 3.6167C4.45556 3.6167 4.61111 3.67781 4.73334 3.80003L8 7.0667L11.2667 3.80003C11.3889 3.67781 11.5444 3.6167 11.7333 3.6167C11.9222 3.6167 12.0778 3.67781 12.2 3.80003C12.3222 3.92225 12.3833 4.07781 12.3833 4.2667C12.3833 4.45559 12.3222 4.61114 12.2 4.73337L8.93334 8.00003L12.2 11.2667C12.3222 11.3889 12.3833 11.5445 12.3833 11.7334C12.3833 11.9223 12.3222 12.0778 12.2 12.2C12.0778 12.3223 11.9222 12.3834 11.7333 12.3834C11.5444 12.3834 11.3889 12.3223 11.2667 12.2L8 8.93337Z"
            fill="#FAF7F5"
          />
        </svg>
        </div>
      )}
    </div>
  );
};

export default EmptyWaveform;